import React, { useState } from "react";
import { ArrowRight, ChevronDown, CheckCircle2, ShieldCheck, Building2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import NewsletterForm from "../components/NewsletterForm";

export default function Home() {
  const { t, raw } = useI18n();
  const pillars = raw("pillars.items") || [];
  const steps = raw("approach.steps") || [];
  const testimonials = raw("testimonials.items") || [];
  const faq = raw("faq.items") || [];
  const trust = raw("hero.trust_items") || [];
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-24">
          <div className="lg:col-span-7">
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-widest text-slate-700"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-amber-700" aria-hidden="true" />
              {t("hero.badge")}
            </span>
            <h1
              className="mt-6 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
              style={{
                fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif",
                lineHeight: 1.05,
              }}
              data-testid="hero-title"
            >
              {t("hero.title_line_1")}
              <br />
              <span className="text-amber-700">{t("hero.title_line_2")}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-slate-600 sm:text-xl">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#newsletter"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
              >
                {t("hero.cta_primary")}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="#approach"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:border-slate-900"
              >
                {t("hero.cta_secondary")}
              </a>
            </div>
            <div className="mt-10">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t("hero.trust_label")}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-slate-500">
                {trust.map((it) => (
                  <span key={it}>{it}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <img
                src="/images/hero-boardroom.jpg"
                alt={t("hero.image_alt")}
                data-testid="hero-image"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS / SERVICES */}
      <section id="services" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">
              {t("pillars.eyebrow")}
            </div>
            <h2
              className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
              style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
            >
              {t("pillars.title")}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{t("pillars.subtitle")}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {pillars.map((p, i) => (
              <div
                key={i}
                data-testid={`pillar-${i}`}
                className="rounded-xl border border-slate-200 bg-slate-50 p-6"
              >
                <div className="text-3xl font-extrabold text-amber-700"
                  style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
                >
                  0{i + 1}
                </div>
                <h3 className="mt-3 text-xl font-semibold text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section id="approach" className="border-b border-slate-200 bg-slate-950 text-slate-100">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-10 lg:py-24">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-500">
              {t("approach.eyebrow")}
            </div>
            <h2
              className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl"
              style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
            >
              {t("approach.title")}
            </h2>
            <p className="mt-4 text-lg text-slate-300">{t("approach.subtitle")}</p>
            <div className="mt-8 overflow-hidden rounded-2xl border border-slate-800">
              <img
                src="/images/paris-hq.jpg"
                alt={t("approach.image_alt")}
                data-testid="approach-image"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <ol className="space-y-6">
              {steps.map((s, i) => (
                <li
                  key={i}
                  data-testid={`approach-step-${i}`}
                  className="grid gap-3 rounded-xl border border-slate-800 bg-slate-900 p-6 sm:grid-cols-[80px_1fr]"
                >
                  <div className="text-3xl font-extrabold text-amber-500"
                    style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
                  >
                    {s.number}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{s.title}</h3>
                    <p className="mt-1 text-sm text-slate-300">{s.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS / INSIGHTS */}
      <section id="insights" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">
              {t("testimonials.eyebrow")}
            </div>
            <h2
              className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
              style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
            >
              {t("testimonials.title")}
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {testimonials.map((tst, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-slate-50 p-6"
              >
                <blockquote className="text-base leading-relaxed text-slate-800">
                  “{tst.quote}”
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img
                    src={`/images/team-portrait-${i + 1}.jpg`}
                    alt={tst.image_alt}
                    className="h-12 w-12 rounded-full border border-slate-300 object-cover"
                    loading="lazy"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{tst.author}</div>
                    <div className="text-xs text-slate-600">{tst.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER (Lead capture) */}
      <section id="newsletter" className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-10 lg:py-24">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">
              {t("newsletter.eyebrow")}
            </div>
            <h2
              className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
              style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
              data-testid="newsletter-title"
            >
              {t("newsletter.title")}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{t("newsletter.description")}</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              {(raw("hero.trust_items") || []).slice(0, 3).map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" aria-hidden="true" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <NewsletterForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">
            {t("faq.eyebrow")}
          </div>
          <h2
            className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
            style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
          >
            {t("faq.title")}
          </h2>
          <div className="mt-10 divide-y divide-slate-200 rounded-2xl border border-slate-200">
            {faq.map((item, i) => {
              const isOpen = openIdx === i;
              return (
                <div key={i} data-testid={`faq-item-${i}`}>
                  <button
                    type="button"
                    onClick={() => setOpenIdx(isOpen ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="text-base font-semibold text-slate-900">{item.q}</span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-slate-500 ${isOpen ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 text-sm leading-relaxed text-slate-700">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-slate-950">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-14 sm:px-6 lg:flex-row lg:items-center lg:px-10">
          <div>
            <h2
              className="text-2xl font-bold text-white sm:text-3xl"
              style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
            >
              {t("cta_banner.title")}
            </h2>
            <p className="mt-2 max-w-2xl text-slate-300">{t("cta_banner.subtitle")}</p>
          </div>
          <a
            href="#newsletter"
            data-testid="cta-banner-button"
            className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-500"
          >
            {t("cta_banner.button")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>
    </main>
  );
}
